import React, { useEffect, useState, useMemo, useRef } from 'react';
import CreatableSelect from 'react-select/creatable';

import ModalContainer from '@/components/modals/ModalContainer';
import CommonImage from '@/components/CommonImage';
import SearchWithAddColor from '@/components/colors/SearchWithAddColor';
import { useGetAllUserQuery } from '@/api/commonApis';
import { toastError } from '@/components/Toast';
import { getUserInfo } from '@/utils/Util';
import useClickOutside from '@/hooks/useClickOutside';

const INITIAL_ROW = {
	id: 'ROW_1',
	color: null,
	colorDetails: '',
	size: '',
	quantity: '',
	sampleItemDetailsRecipientMapResponse: []
};

const ColorSizeQtyModalV2 = ({
	onClose,
	onUpdateSubmit,
	productId,
	requestType = '',
	handoverModalFor,
	colorResponseList = []
}) => {
	const [rows, setRows] = useState([INITIAL_ROW]);
	const [selectedColorRow, setSelectedColorRow] = useState(null);
	const [isShowTooltip, setIsShowTooltip] = useState(true);
	const [errors, setErrors] = useState([]);
	const { data: userList } = useGetAllUserQuery({ params: { page: 0, size: 1000 } });
	const colorPickerRef = useRef();
	const userDismissedTooltipRef = useRef(false);

	useClickOutside(colorPickerRef, () => setSelectedColorRow(null));

	// Sort user list: logged-in user first, then user with id 12561, then rest
	const sortedUserList = useMemo(() => {
		if (!userList || userList.length === 0) return [];

		const loggedInUser = getUserInfo();
		const loggedInUserId = loggedInUser?.id;
		const secondUserId = 12561;

		return [...userList].sort((a, b) => {
			// Logged-in user should be first
			if (a.id === loggedInUserId) return -1;
			if (b.id === loggedInUserId) return 1;

			// User with id 12561 should be second
			if (a.id === secondUserId) return -1;
			if (b.id === secondUserId) return 1;

			// Keep the rest as is
			return 0;
		});
	}, [userList]);

	useEffect(() => {
		const updatedColorRes = colorResponseList?.map((item, index) => ({
			...item,
			id: `ROW_${index + 1}`,
			color: {
				name: item?.name,
				id: item?.id,
				code: item?.code,
				hexCode: item?.hexCode,
				pantoneColorId: item?.pantoneColorId
			},
			colorDetails: item?.code || item?.name,
			size: item?.size,
			quantity: item?.quantity,
			sampleItemDetailsRecipientMapResponse: item?.sampleItemDetailsRecipientMapResponse?.map(
				(user, index) => ({
					...user,
					id: user?.id || user?.userResponse?.id,
					value: `${user}_${index}`,
					label: user?.name || user?.userResponse?.name
				})
			)
		}));

		const hasAnyFilledRow = updatedColorRes?.some(
			(row) =>
				row?.quantity > 0 ||
				(row?.size && String(row.size).trim()) ||
				row?.color?.id ||
				(row?.colorDetails && String(row.colorDetails).trim()) ||
				row?.name
		);

		if (updatedColorRes && updatedColorRes.length > 0 && hasAnyFilledRow) {
			setRows(updatedColorRes);
		} else {
			setRows([INITIAL_ROW]);
		}
	}, [requestType, colorResponseList]);

	useEffect(() => {
		if (userDismissedTooltipRef.current) return;

		const isRecipientSelected = rows?.some(
			(item) => item?.sampleItemDetailsRecipientMapResponse?.length > 0
		);

		if (isRecipientSelected) {
			setIsShowTooltip(false);
		} else {
			setIsShowTooltip(true);
		}
	}, [JSON.stringify(rows)]);

	const isValidForm = () => {
		const newErrors = [];
		const hasAnyQty = [...rows]?.some((item) => item?.quantity > 0);
		if (!hasAnyQty) {
			toastError('Sample quantity must equal the number of selected persons.');
			newErrors.push('ROW_1');
		}
		[...rows]?.forEach((item) => {
			// user and qty should be same
			if (
				isShowOwnerDropdown() &&
				parseInt(item.quantity) > 0 &&
				parseInt(item?.sampleItemDetailsRecipientMapResponse?.length) !== parseInt(item.quantity)
			) {
				toastError('Please select owners equal to the quantity.');
				newErrors.push(item?.id);
			}
			if (isShowOwnerDropdown() && parseInt(item?.sampleItemDetailsRecipientMapResponse?.length) > 4) {
				toastError('You can not select more then 4 user');
				newErrors.push(item?.id);
			}
		});

		// At least one row qty is required
		const isAnyQtyAvailable = rows.some((item) => item?.quantity > 0);

		if (!isAnyQtyAvailable) {
			newErrors.push('ROW_1');
		}

		setErrors(newErrors);
		return newErrors?.length === 0;
	};

	const isShowOwnerDropdown = () => {
		return (
			requestType === 'SEND_TO_PD' ||
			requestType === 'SAMPLE' ||
			requestType === 'SEND_TO_FACTORY_BUYER_DESIGN' ||
			handoverModalFor === 'TP_HANDOVER_TO_PROJECT_TEAM' ||
			requestType === 'SEND_TO_SAMPLE_SECTION' ||
			requestType === 'SEND_REVISE_SAMPLE_REQUEST' ||
			requestType === 'SEND_TO_FACTORY_NITEX_DESIGN'
		);
	};

	const handleChange = (e, rowId) => {
		const { name, value } = e.target;

		const updatedRows = [...rows]?.map((item) => {
			if (item?.id === rowId) {
				return {
					...item,
					[name]: value
				};
			}
			return item;
		});
		setRows(updatedRows);
	};

	const handleAddColor = (color, rowId) => {
		const updatedRows = [...rows]?.map((item) => {
			if (item.id === rowId) {
				return {
					...item,
					colorDetails: color?.code,
					color
				};
			}
			return item;
		});
		setRows(updatedRows);
		setSelectedColorRow(null);
	};

	const handleSampleOwners = (owners, rowId) => {
		const values = owners?.map((owner, index) => ({
			...owner,
			value: `${owner?.value}_${index}`
		}));
		const updatedRows = [...rows]?.map((item) => {
			if (item?.id === rowId) {
				return {
					...item,
					sampleItemDetailsRecipientMapResponse: values
				};
			}
			return item;
		});
		setRows(updatedRows);
	};

	const handleSubmit = (e) => {
		e.stopPropagation();
		if (!isValidForm()) return;
		onUpdateSubmit?.({ [productId]: rows });
		onClose?.();
	};

	const customStyles = {
		control: (base) => ({
			...base,
			border: '0px',
			boxShadow: 'none',
			outline: 'none',
			minHeight: '40px',
			paddingLeft: '0px',
			cursor: 'pointer',
			background: 'transparent',
			'&:hover': {
				border: '0px',
				boxShadow: 'none',
				background: 'transparent'
			}
		})
	};
	const addNewRowHandler = () => {
		userDismissedTooltipRef.current = true;
		setIsShowTooltip(false);
		const newRow = {
			id: `ROW_${rows?.length + 1}`,
			color: null,
			colorDetails: '',
			size: '',
			quantity: '',
			sampleItemDetailsRecipientMapResponse: []
		};
		setRows([...rows, newRow]);
	};
	const handleRemoveRow = (rowId) => {
		const updateRows = [...rows]?.filter((item) => item.id !== rowId);
		setRows(updateRows);
	};

	return (
		<ModalContainer className="update-sample-detail-modal">
			<div className="popup-wrapper shadow-16dp">
				<div className="common-popup update-sample-detail-modal">
					<div className="common-popup-header d-flex justify-content-between">
						<div className="popup-tilte">
							<h3 className="semibold-16 mb-0">Update Sample details</h3>
						</div>
						<div
							className="close-btn"
							onClick={(e) => {
								e.stopPropagation();
								onClose?.();
							}}
						>
							<CommonImage alt="close" src="/icons/close.svg" />
						</div>
					</div>
					<div className="common-popup-body scroll-y-label">
						<div className="update-sample-detail-table">
							<table className="table height-sm with-border no-hover mb-1">
								<thead>
									<tr>
										<th>Color</th>
										<th>Color details</th>
										<th>Size</th>
										<th>Qty</th>
										{isShowOwnerDropdown() && (
											<th>
												{isShowTooltip && (
													<span className="custom-tooltip">
														<span className="tooltiptext">
															Sample quantity must equal the <br /> number of
															selected persons. If a person <br /> needs two
															samples, select his name twice.
														</span>
													</span>
												)}

												<p>Whom to send sample</p>
											</th>
										)}
									</tr>
								</thead>
								<tbody>
									{rows?.map((row) => (
										<tr key={row?.id}>
											<td className="color-column cursor-pointer position-relative">
												<div
													onClick={() => {
														setSelectedColorRow(row);
													}}
												>
													{row?.color?.hexCode ? (
														<span
															className="color-icon"
															style={{ background: row?.color?.hexCode }}
														/>
													) : (
														<CommonImage
															alt="color"
															src="/icons/Add.svg"
															Width={24}
															Height={24}
														/>
													)}
												</div>
												{selectedColorRow?.id === row?.id && (
													<div
														className="color-and-code-popup position-absolute left-0 bg-white px-2 py-3 shadow-16dp"
														ref={colorPickerRef}
													>
														<SearchWithAddColor
															onEditClick={() => {}}
															selectedColorList={[]}
															onAddNewColor={(item) => {
																handleAddColor(item, row?.id);
															}}
														/>
													</div>
												)}
											</td>
											<td className="color-details-column">
												<input
													name="colorDetails"
													value={row?.colorDetails}
													type="text"
													className="shadow-none"
													onChange={(e) => handleChange(e, row?.id)}
												/>
											</td>
											<td className="size-column">
												<input
													name="size"
													value={row?.size}
													type="text"
													onChange={(e) => handleChange(e, row?.id)}
													className="shadow-none"
												/>
											</td>
											<td className="qty-column">
												<input
													className={`shadow-none ${errors?.includes(row?.id) ? 'common-border-error' : ''}`}
													name="quantity"
													value={row?.quantity}
													onChange={(e) => handleChange(e, row?.id)}
													type="number"
													min={1}
													max={4}
												/>
											</td>
											{isShowOwnerDropdown() && (
												<td
													className="who-column"
													onClick={() => {
														userDismissedTooltipRef.current = true;
														setIsShowTooltip(false);
													}}
												>
													<div
														className={`form-group no-shadow-react-select ${errors?.includes(row?.id) ? 'common-border-error' : ''}`}
													>
														<CreatableSelect
															placeholder="Select name"
															onChange={(value) =>
																handleSampleOwners(value, row?.id)
															}
															value={row?.sampleItemDetailsRecipientMapResponse}
															options={sortedUserList?.map((user) => ({
																...user,
																name: `${user?.nickName} ${user?.country?.code ? `| ${user?.country?.code}` : ''}`,
																value: `${user?.nickName} ${user?.country?.code ? `| ${user?.country?.code}` : ''}`,
																label: `${user?.nickName} ${user?.country?.code ? `| ${user?.country?.code}` : ''}`
															}))}
															isMulti
															isValidNewOption={() => false}
															isClearable={false}
															styles={customStyles}
														/>
													</div>
													{rows?.length > 1 && row?.id !== 'ROW_1' && (
														<span
															className="cursor-pointer remove-row-icon"
															onClick={() => handleRemoveRow(row?.id)}
														>
															<CommonImage
																alt="close"
																src="/icons/close.svg"
																Width={20}
																Height={20}
															/>
														</span>
													)}
												</td>
											)}
										</tr>
									))}
								</tbody>
							</table>
							<button className="button text size32 pl-0" onClick={addNewRowHandler}>
								<CommonImage alt="update" src="/icons/add-round.svg" />
								<span className="ml-1">Add new colorway</span>
							</button>
						</div>
					</div>
					<div className="modal-footer">
						<div className="footer-buttons text-right">
							<button
								className="button outline size32 ml-2"
								onClick={(e) => {
									e.stopPropagation();
									onClose?.();
								}}
							>
								Cancel
							</button>
							<button className="button size32 ml-2" onClick={(e) => handleSubmit(e)}>
								Update
							</button>
						</div>
					</div>
				</div>
			</div>
		</ModalContainer>
	);
};

export default ColorSizeQtyModalV2;
